import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  ButtonGroup,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip
} from '@mui/material';
import { IsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import ImportFileUploader from 'components/ui/file-uploader/ImportFileUploader';
import TenantPage from 'layouts/TenantPage';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { laboratoryService, tenantSettingsService } from 'shared/services';
import { operationalUnitService } from 'features/settings/topology/api/operational-unit.service';
import { sectorService } from '../api/sector.service';
import { AddButton, LinkButton } from 'components/ui';
import { EnhancedDialog } from 'components/enhanced';

export const Topology = () => {
  const translationPrefix = 'pages.topology.main';
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [entityToAdd, setEntityToAdd] = useState({
    name: 'laboratory',
    code: '',
    description: '',
    callback: (entity) => null
  });
  const [selected, setSelected] = useState({
    district: 1,
    lab: null,
    operational_unit: null,
    sector: null,
    warehouse: null,
    location: null
  });
  const [laboratories, setLaboratories] = useState([]);
  const [operationalUnits, setOperationalUnits] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);

  const loadOperationalUnits = (labId) => {
    setSelected({ ...selected, lab: labId });
    setWarehouses([]);
    setLocations([]);
    operationalUnitService
      .getAllBaseInformation({ laboratory: labId })
      .then((res) => setOperationalUnits(res));
  };

  const loadSectors = (opUnitId) => {
    setSelected({ ...selected, operational_unit: opUnitId });
    setWarehouses([]);
    setLocations([]);
    sectorService
      .getAllBaseInformation({ operational_unit: opUnitId })
      .then((res) => setSectors(res));
  };

  // Add entity
  const newLaboratory = () => {
    setEntityToAdd({
      name: 'laboratory',
      code: '',
      description: '',
      callback: (entity) => {
        laboratoryService
          .add({
            id: null,
            code: entity.code,
            description: entity.description
          })
          .then((res) => {
            setLaboratories([...laboratories, res]);
          });
      }
    });
    setOpen(true);
  };

  const newOperationalUnit = () => {
    setEntityToAdd({
      name: 'operationalunit',
      code: '',
      description: '',
      callback: (entity) => {
        operationalUnitService
          .add({
            id: null,
            code: entity.code,
            description: entity.description,
            laboratory: selected.lab
          })
          .then((res) => {
            setOperationalUnits([...operationalUnits, res]);
          });
      }
    });
    setOpen(true);
  };
  const newSector = () => {
    setEntityToAdd({
      name: 'sector',
      code: '',
      description: '',
      callback: (entity) => {
        sectorService
          .add({
            id: null,
            code: entity.code,
            description: entity.description,
            operational_unit: selected.operational_unit
          })
          .then((res) => {
            setSectors([...sectors, res]);
          });
      }
    });
    setOpen(true);
  };

  const saveEntity = () => {
    entityToAdd.callback(entityToAdd);
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    laboratoryService.getAllBaseInformation().then((res) => setLaboratories(res));
  }, []);

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      menuRight={
        <ButtonGroup>
          <IsTenantAdministrator>
            <ImportFileUploader service={tenantSettingsService} />
          </IsTenantAdministrator>
          <Tooltip title={t(`${translationPrefix}.to-tree-view`)}>
            <LinkButton to="tree" icon={AccountTreeIcon} />
          </Tooltip>
          <AddButton
            onClick={newLaboratory}
            disabled={!selected.district}
            label={t(`global.laboratory`)}
          />
          <AddButton
            onClick={newOperationalUnit}
            disabled={!selected.lab}
            label={t(`global.operational_unit`)}
          />
          <AddButton
            onClick={newSector}
            disabled={!selected.operational_unit}
            label={t(`global.sector`)}
          />
        </ButtonGroup>
      }>
      <div className="flex">
        {[
          { key: 'laboratory', data: laboratories, loadMethod: loadOperationalUnits },
          { key: 'operationalunit', data: operationalUnits, loadMethod: loadSectors },
          { key: 'sector', data: sectors, loadMethod: null }
        ].map((entity) => (
          <div className="w-1/3 border-r border-slate-200" key={entity.key}>
            <h3 className="font-bold border-b border-slate-200">{t(`global.${entity.key}`)}</h3>
            {entity.data.map((item) => (
              <ListItemButton selected={item.id === selected[entity.key]} key={item.id}>
                <ListItem
                  disableGutters
                  onClick={() => entity.loadMethod && entity.loadMethod(item.id)}
                  onDoubleClick={() => navigate(`${entity.key}/${item.id}`)}
                  secondaryAction={
                    <div>
                      {entity.loadMethod && (
                        <IconButton onClick={() => entity.loadMethod(item.id)}>
                          <ArrowForwardIosIcon />
                        </IconButton>
                      )}
                    </div>
                  }>
                  <ListItemText primary={item.description} secondary={item.code} />
                </ListItem>
              </ListItemButton>
            ))}
          </div>
        ))}
      </div>
      <EnhancedDialog
        open={open}
        onClose={handleClose}
        onSubmit={saveEntity}
        maxWidth="xs"
        title={t('pages.topology.main.add-new-modal.title', {
          entityName: t(`global.${entityToAdd.name}`).toLowerCase()
        })}
        subTitle={t('pages.topology.main.add-new-modal.body')}>
        <TextField
          autoFocus
          margin="dense"
          id="code"
          label={t(`global.code`)}
          fullWidth
          variant="standard"
          value={entityToAdd.code}
          onChange={(e) => setEntityToAdd({ ...entityToAdd, code: e.target.value })}
        />
        <TextField
          autoFocus
          margin="dense"
          id="description"
          label={t(`global.description`)}
          fullWidth
          variant="standard"
          value={entityToAdd.description}
          onChange={(e) => setEntityToAdd({ ...entityToAdd, description: e.target.value })}
        />
      </EnhancedDialog>
    </TenantPage>
  );
};

export default Topology;
